import React, { useEffect, useState } from 'react';
import { Edit2, Plus, Save, Trash2, X } from 'lucide-react';
import api from '../lib/api';
import ImageUploadField from '../components/ImageUploadField';
import HelpCallout from './HelpCallout';
import TrashPanel from './TrashPanel';

function empty() {
  return { slug: '', title: '', subtitle: '', description: '', hero_image: '', price_from: '', includes: [], book_url: 'https://www.stayplayexplore.co.uk/', badge: '', featured: true };
}

export default function AdminPackages() {
  const [items, setItems] = useState([]);
  const [editing, setEditing] = useState(null);
  const [creating, setCreating] = useState(false);

  function load() { api.get('/admin/packages').then((r) => setItems(r.data.items)); }
  useEffect(load, []);

  async function save() {
    try {
      let payload = { ...editing };
      if (typeof payload.includes === 'string') payload.includes = payload.includes.split('\n').map((s) => s.trim()).filter(Boolean);
      if (!Array.isArray(payload.includes)) payload.includes = [];
      if (!payload.title?.trim()) { alert('Title is required'); return; }
      if (creating) {
        if (!payload.slug) payload.slug = payload.title.toLowerCase().replace(/[^a-z0-9]+/g, '-').replace(/^-|-$/g, '');
        await api.post('/admin/packages', payload);
      } else {
        delete payload.id; delete payload.created_at;
        await api.patch(`/admin/packages/${editing.slug}`, payload);
      }
      setEditing(null); setCreating(false); load();
    } catch (e) {
      alert(`Save failed: ${e.response?.data?.detail || e.message}`);
    }
  }
  async function del(slug) {
    if (!window.confirm('Delete package?')) return;
    await api.delete(`/admin/packages/${slug}`); load();
  }

  return (
    <div>
      <div className="flex items-center justify-between mb-8">
        <div>
          <div className="label text-heritage mb-2">StayPlayExplore</div>
          <h1 className="font-display text-4xl">Packages ({items.length})</h1>
        </div>
        <button onClick={() => { setEditing(empty()); setCreating(true); }} data-testid="admin-package-new" className="bg-forest text-base px-5 py-2.5 rounded-full text-sm font-semibold inline-flex items-center gap-2 hover:bg-forest-deep">
          <Plus size={14}/> Add package
        </button>
      </div>

      <HelpCallout
        title="Where StayPlayExplore breaks earn their place on the site."
        description="Each package becomes a clickable card linking out to stayplayexplore.co.uk. The 'Featured' tick promotes a package into the homepage hero promo block — keep your headline offer ticked here."
        appearsAt={[
          { label: 'Homepage "Stay & save" promo block', where: 'packages with Featured ticked appear in the heritage-red section' },
          { label: '/packages page', where: 'every package, sorted with featured ones first' },
          { label: 'Header CTA "Book a break"', where: 'links to /packages' },
        ]}
      />

      <TrashPanel
        resourcePath="/admin/packages"
        resourceLabel="Package"
        idField="slug"
        nameField="title"
        onChange={load}
      />

      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        {items.map((p) => (
          <div key={p.id} className="bg-white border border-charcoal/10 rounded-2xl overflow-hidden" data-testid={`admin-package-${p.slug}`}>
            <div className="aspect-[16/9] overflow-hidden bg-stone">{p.hero_image && <img src={p.hero_image} alt="" className="w-full h-full object-cover"/>}</div>
            <div className="p-6">
              <div className="label text-heritage mb-2">{p.price_from} · {p.badge}</div>
              <h3 className="font-display text-2xl mb-1">{p.title}</h3>
              <p className="text-muted text-sm mb-4">{p.subtitle}</p>
              <div className="flex gap-2">
                <button onClick={() => { setEditing({ ...p, includes: (p.includes || []).join('\n') }); setCreating(false); }} className="flex-1 bg-admin border border-charcoal/15 px-3 py-2 rounded-full text-xs font-semibold inline-flex items-center justify-center gap-1"><Edit2 size={12}/> Edit</button>
                <button onClick={() => del(p.slug)} className="bg-admin border border-heritage/30 text-heritage px-3 py-2 rounded-full text-xs font-semibold"><Trash2 size={12}/></button>
              </div>
            </div>
          </div>
        ))}
      </div>

      {editing && (
        <div className="fixed inset-0 bg-charcoal/60 backdrop-blur-sm z-50 flex items-center justify-center p-4">
          <div className="bg-white rounded-3xl max-w-2xl w-full max-h-[90vh] overflow-y-auto">
            <div className="sticky top-0 bg-white border-b border-charcoal/10 p-6 flex items-center justify-between">
              <h3 className="font-display text-2xl">{creating ? 'New package' : editing.title}</h3>
              <button onClick={() => { setEditing(null); setCreating(false); }} className="p-2 hover:bg-stone rounded-lg"><X size={18}/></button>
            </div>
            <div className="p-6 space-y-4">
              <label className="block"><div className="label mb-1.5">Title</div><input value={editing.title} onChange={(e) => setEditing({ ...editing, title: e.target.value })} data-testid="pkg-title-input" className="w-full bg-admin border border-charcoal/15 rounded-xl px-4 py-2.5"/></label>
              <label className="block"><div className="label mb-1.5">Subtitle</div><input value={editing.subtitle || ''} onChange={(e) => setEditing({ ...editing, subtitle: e.target.value })} className="w-full bg-admin border border-charcoal/15 rounded-xl px-4 py-2.5"/></label>
              <div className="grid grid-cols-2 gap-4">
                <label className="block"><div className="label mb-1.5">Price from</div><input value={editing.price_from || ''} onChange={(e) => setEditing({ ...editing, price_from: e.target.value })} className="w-full bg-admin border border-charcoal/15 rounded-xl px-4 py-2.5"/></label>
                <label className="block"><div className="label mb-1.5">Badge</div><input value={editing.badge || ''} onChange={(e) => setEditing({ ...editing, badge: e.target.value })} className="w-full bg-admin border border-charcoal/15 rounded-xl px-4 py-2.5"/></label>
              </div>
              <ImageUploadField
                value={editing.hero_image}
                onChange={(v) => setEditing({ ...editing, hero_image: v })}
                label="Hero image"
                testidPrefix="pkg-hero"
              />
              <label className="block"><div className="label mb-1.5">Description</div><textarea rows={3} value={editing.description || ''} onChange={(e) => setEditing({ ...editing, description: e.target.value })} className="w-full bg-admin border border-charcoal/15 rounded-xl px-4 py-2.5"/></label>
              <label className="block"><div className="label mb-1.5">Includes (one per line)</div><textarea rows={4} value={editing.includes || ''} onChange={(e) => setEditing({ ...editing, includes: e.target.value })} className="w-full bg-admin border border-charcoal/15 rounded-xl px-4 py-2.5 font-mono text-sm"/></label>
              <label className="block"><div className="label mb-1.5">Book URL</div><input value={editing.book_url || ''} onChange={(e) => setEditing({ ...editing, book_url: e.target.value })} className="w-full bg-admin border border-charcoal/15 rounded-xl px-4 py-2.5"/></label>
              <label className="flex items-center gap-3 bg-admin border border-charcoal/10 rounded-xl px-4 py-3"><input type="checkbox" checked={!!editing.featured} onChange={(e) => setEditing({ ...editing, featured: e.target.checked })}/><span className="text-sm font-semibold">Featured on homepage</span></label>
            </div>
            <div className="sticky bottom-0 bg-white border-t border-charcoal/10 p-6 flex justify-end gap-3">
              <button onClick={() => { setEditing(null); setCreating(false); }} className="px-5 py-2.5 rounded-full font-semibold border border-charcoal/15">Cancel</button>
              <button onClick={save} data-testid="package-save" className="bg-forest text-base px-6 py-2.5 rounded-full font-semibold inline-flex items-center gap-2 hover:bg-forest-deep"><Save size={14}/> Save</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
